// src/views/Users/Customers/Customers.jsx
import { useState, useEffect, useRef } from "react";
import { globalConstants, modeConstants } from "../../../constants";
import AddEmployeeModal from "../../../shared/EmployeeModal";
import { useDispatch, useSelector } from "react-redux";
import ActionsDataTable from "../../../shared/ActionsDataTable";
import { employeeActions } from "../../../redux/actions";
import { getUserData, SCREENS_IDS } from "../../../system";

export default function Employees() {
  const initialGridState = {
    column: "",
    type: "",
    search: null,
    filters: [],
  };

  const dispatch = useDispatch();
  const sortingInProgressRef = useRef(false);
  const decryptedData = getUserData();

  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(initialGridState.pageNumber);
  // modal + row state
  const [mode, setMode] = useState(modeConstants.ADD_MODE);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [rowData, setRowData] = useState([]);
  let allowToolbar = decryptedData.user_permissions.some(
    (perm) => perm.permission_id === SCREENS_IDS.CAN_CREATE_EMPLOYEE
  );

  let allowEdit = decryptedData.user_permissions.some(
    (perm) => perm.permission_id === SCREENS_IDS.CAN_EDIT_EMPLOYEE
  );

  let allowDelete = decryptedData.user_permissions.some(
    (perm) => perm.permission_id === SCREENS_IDS.CAN_DELETE_EMPLOYEE
  );
  const allData = useSelector((s) => s.employeeReducer.data);
  // filters aligned to table fields
  const filterColumns = [
    { key: "name", label: "Name", type: "string" },
    { key: "email", label: "Email", type: "string" },
    { key: "phone_number", label: "Phone Number", type: "number" },
    {
      key: "passport_expiry_date",
      label: "Passport Expiry Date",
      type: "date",
    },
    { key: "visa_expiry_date", label: "Visa Expiry Date", type: "date" },
    { key: "contract_end_date", label: "Contact Expiry Date", type: "date" },
  ];

  const columns = [
    {
      name: "Sr. No",
      selector: (row, index) => index + 1, // start from 1
      sortable: false,
      width: "100px",
    },
    {
      id: "profile_pic_url",
      name: "Profile Pic",
      sortable: false,
      width: "150px",
      cell: (row) => {
        const url = row?.profile_pic_url;

        return url ? (
          <img
            src={`${process.env.REACT_APP_STORAGE_URL}${url}`}
            alt="Profile"
            style={{ width: "50px", height: "50px" }}
            className="my-3 rounded-circle"
          />
        ) : (
          <span className="text-muted">No Image</span>
        );
      },
    },
    {
      id: "fullName",
      name: "Name",
      selector: (r) => r.fullName,
      sortable: true,
      grow: 1.6,
    },
    {
      id: "email",
      name: "Email",
      cell: (r) => <div className="email-wrap">{r.email}</div>,
      sortable: true,
      grow: 1.6,
    },
    {
      id: "phone_number",
      name: "Phone Number",
      selector: (r) => r.phone_number,
      sortable: true,
      width: "160px",
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span>{row.phone_number}</span>
        </div>
      ),
    },
    {
      id: "passport_document_url",
      name: "Passport Document",
      sortable: false,
      width: "150px",
      cell: (row) => {
        const url = row?.passport_document_url;
        return url ? (
          <a
            href={process.env.REACT_APP_STORAGE_URL + url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-danger fs-5"
            title=""
          >
            <i className="bi bi-eye"></i>
          </a>
        ) : (
          <span>--</span>
        );
      },
    },
    {
      id: "visa_document_url",
      name: "Visa Document",
      sortable: false,
      width: "150px",
      cell: (row) => {
        const url = row?.visa_document_url;
        return url ? (
          <a
            href={process.env.REACT_APP_STORAGE_URL + url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-danger fs-5"
            title=""
          >
            <i className="bi bi-eye"></i>
          </a>
        ) : (
          <span>--</span>
        );
      },
    },
    {
      id: "brp_document_url",
      name: "BRP Document",
      sortable: false,
      width: "150px",
      cell: (row) => {
        const url = row?.brp_document_url;
        return url ? (
          <a
            href={process.env.REACT_APP_STORAGE_URL + url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-danger fs-5"
            title=""
          >
            <i className="bi bi-eye"></i>
          </a>
        ) : (
          <span>--</span>
        );
      },
    },
    {
      id: "contract_document_url",
      name: "Contract Document",
      sortable: false,
      width: "150px",
      cell: (row) => {
        const url = row?.contract_document_url;
        return url ? (
          <a
            href={process.env.REACT_APP_STORAGE_URL + url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-danger fs-5"
            title=""
          >
            <i className="bi bi-eye"></i>
          </a>
        ) : (
          <span>--</span>
        );
      },
    },
    // Toggle
    {
      id: "is_active",
      name: "IsActive",
      sortable: false,
      width: "110px",
      selector: (r) => r.is_active,
      cell: (row) => (
        <div
          className="form-check form-switch m-0"
          onClick={(e) => e.stopPropagation()}
          title={row.is_active == 1 ? "Active" : "Inactive"}
        >
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={row.is_active == 1 ? true : false}
            onChange={() => {
              toggleStatus(row.id);
            }}
            aria-checked={row.is_active == 1 ? true : false}
          />
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    // Actions
    {
      name: "Actions",
      sortable: false,
      width: "170px",
      cell: (row) => (
        <div
          className="d-flex align-items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {allowEdit && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              title="Edit"
              aria-label="Edit"
              onClick={() => {
                getRowData(row, modeConstants.UPDATE_MODE);
                setShowEmployeeModal(true);
              }}
            >
              <i className="bi bi-pencil" />
            </button>
          )}
          {allowDelete && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              title="Edit"
              aria-label="Edit"
              onClick={() => {
                deleteRow(row);
              }}
            >
              <i className="bi bi-trash" />
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const getRowData = (row, nextMode) => {
    dispatch(employeeActions.getEmployeeById(row.id));
    setMode(nextMode);
  };

  const toggleStatus = (id) => {
    dispatch(employeeActions.deleteEmployeeById(id));
  };

  const deleteRow = (row) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this employee permanently?"
    );

    if (isConfirmed) {
      dispatch(employeeActions.deleteEmployeePermanentById(row.id));
    }
  };

  const onSort = (sortedColumn, sortDirection) => {
    sortingInProgressRef.current = true;
    const columnDirection = sortDirection
      ? sortDirection.toUpperCase() // "ASC" | "DESC"
      : null;
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy:
        columnDirection !== null ? `${sortedColumn.id} ${columnDirection}` : "",
    }));
  };

  const onChangePage = (pageNumber) => {
    if (!sortingInProgressRef.current) {
      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  const getAll = () => {
    dispatch(employeeActions.getAllEmployee(gridState));
  };
  useEffect(() => {
    if (allData !== null) {
      setRowData(allData);
    }
    // eslint-disable-next-line
  }, [allData]);

  useEffect(() => {
    getAll();
    sortingInProgressRef.current = false;
    // eslint-disable-next-line
  }, [gridState]);

  useEffect(() => {
    getAll();
  }, []);

  return (
    <>
      <AddEmployeeModal
        show={showEmployeeModal}
        onHide={() => setShowEmployeeModal(false)}
        mode={mode}
      />

      <ActionsDataTable
        columns={columns}
        filterColumns={filterColumns}
        data={rowData}
        pagination
        highlightOnHover
        striped
        responsive
        persistTableHead
        selectableRows
        selectableRowsHighlight
        selectableRowsVisibleOnly
        paginationServer={false}
        onSort={onSort}
        gridState={gridState}
        paginationTotalRows={rowData.length}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        paginationDefaultPage={defaultPage}
        paginationResetDefaultPage
        setShowModal={setShowEmployeeModal}
        setMode={setMode}
        setGridState={setGridState}
        allowToolbar={allowToolbar}
        title="Employees"
      />
    </>
  );
}
